import React, { useEffect, useState, useRef } from 'react';
import { connect } from "react-redux";
import Controls from './components/Controls/Controls';
import Room from './components/Room';
import useRoomState from './hooks/useRoomState';
import useVideoContext from './hooks/useVideoContext';
import useDeviceSettingsToggle from './hooks/useDeviceSettingsToggle';
import useAddGuestToggle from './hooks/useAddGuestToggle';
import useIsLocalParticipantReconnecting from './hooks/useIsLocalParticipantReconnecting';
import useIsRemoteParticipantReconnecting from './hooks/useIsRemoteParticipantReconnecting';
import AllowPermissions from './components/AllowPermissions';
import { useTwilioAppState } from './state';
import MiniSpinner from '../../common/MiniSpinner';
import { withRouter } from "react-router-dom";
import useMainSpeaker from './hooks/useMainSpeaker';
import usePublications from './hooks/usePublications';
import useScreenShareParticipant from './hooks/useScreenShareParticipant';
import useShowControls from './hooks/useShowControls';
import GuestLinkModal from "../../care/GuestLinkModal";
import GuestAllowDenyModal from "../../care/GuestAllowDenyModal";
import { Logger } from './utils';
import * as formatting from "../../../helpers/formatting";
import * as types from "../../../constants/actionTypes";

const TwilioVideoApp = (props) => {
    const roomState = useRoomState();
    const { isConnecting, connect, room, getAudioAndVideoTracks, isAcquiringLocalTracks } = useVideoContext();
    const { isFetching, twilioToken, roomName, onConnected, setError, _isMounted } = useTwilioAppState();
    const [isEndCall, setIsEndCall] = useState(false);
    const [showControls, setShowControls] = useShowControls();
    const localParticipantReconnecting = useIsLocalParticipantReconnecting();
    const remoteParticipantReconnecting = useIsRemoteParticipantReconnecting();
    const roomDisconnected = roomState && roomState === 'disconnected';
    const roomConnected = (roomState === 'connected' || (remoteParticipantReconnecting && !roomDisconnected));
    const mainParticipant = useMainSpeaker();
    let showVideoBackground = false;
    const publications = usePublications(mainParticipant);
    const videoOrScreenPublication = publications.find(p => p.trackName.includes('camera')) || publications.find(p => p.trackName.includes('screen'));
    showVideoBackground = videoOrScreenPublication && mainParticipant.state === "connected" ? true : false;
    const [deviceSettingsOpen, toggleDeviceSettings] = useDeviceSettingsToggle();
    const [showGuestLinkModal, toggleGuestLinkModal] = useAddGuestToggle();
    let showBackdrop = showGuestLinkModal || deviceSettingsOpen ? true : false;
    const [guestAcceptDenyData, setGuestAcceptDenyData] = useState({ showGuestAcceptDenyModal: false, guestName: "", roomSharedId: "" });
    const waitingGuestList = props.shareLinkHistory && props.shareLinkHistory.waitingGuestList && props.shareLinkHistory.waitingGuestList.length > 0 ? props.shareLinkHistory.waitingGuestList : [];
    const [waitingGuestStateData, setWaitingGuestList] = useState(waitingGuestList.map(guest => ({ showState: false, visited: false, id: guest.id, guestName: guest.token.participantName })));
    const [showOptions, setShowOptions] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    if (roomState === "reconnecting")
        showVideoBackground = false;

    const screenShareParticipant = useScreenShareParticipant();
    const screenShareParticipantName = screenShareParticipant && screenShareParticipant.identity && screenShareParticipant.identity.split("||") && screenShareParticipant.identity.split("||").length >= 3 && screenShareParticipant.identity.split("||")[3] || "";

    const onBackdropClick = () => {
        Logger(`UI.Twilio.Room.Backdrop.Clicked`, new Date());
        if (showGuestLinkModal) {
            toggleGuestLinkModal(false);
        }
        if (deviceSettingsOpen) {
            toggleDeviceSettings(false);
        }
    }

    useEffect(() => {
        const { id, token, status } = props.guestLinkApprovalStatus || {};
        if (id && token && roomName && roomName === token.roomId && status === "GUEST_WAITING_FOR_APPROVAL") {
            setGuestAcceptDenyData({ showGuestAcceptDenyModal: true, guestName: token.participantName, roomSharedId: id });
        } else if (id && token && roomName && roomName === token.roomId && guestAcceptDenyData && guestAcceptDenyData.showGuestAcceptDenyModal && (status === "GUEST_ALLOWED" || status === "GUEST_NOT_ALLOWED")) {
            setGuestAcceptDenyData(prevState => {
                return {
                    ...prevState,
                    showGuestAcceptDenyModal: false
                };
            });
            props.getShareLinkHistory(roomName);
        }
    }, [props.guestLinkApprovalStatus]);


    useEffect(() => {
        if (twilioToken && roomName && !isAcquiringLocalTracks && !roomConnected) {
            getAudioAndVideoTracks().then(tracks => {
                if (props.error) {
                    setError(null);
                }
                if (tracks && typeof tracks === 'string') { return; } //Avoid race condition. Tracks Already Acquired
                Logger(`UI.Twilio.Room.onTrackFetch.${tracks && tracks.length ? "Success" : "TrackNotFound"}`, new Date(), { tracks }, roomName);
                connect(twilioToken, roomName, tracks && tracks.length ? tracks : null).then(
                    rsp => {
                        if (rsp.isSuccess) {
                            Logger("UI.Twilio.Room.onRoomConnectSuccess", new Date(), { rsp }, roomName);
                            const participants = [];
                            const connectedTime = new Date();
                            rsp?.room?.participants && rsp.room.participants.forEach(p => {
                                const idParts = p.identity?.split('||');
                                if (idParts && idParts.length > 1 && idParts[2] == 'Patient') {
                                    participants.push({ roomId: roomName, participantId: idParts[1], connectedTime });
                                }
                            });
                            props.setParticipants(participants);
                            if (onConnected) {
                                onConnected(rsp.room);
                            }
                        } else {
                            Logger("UI.Twilio.Room.onRoomConnectFailure", new Date(), { rsp }, roomName);
                            if (props.onTwilioRoomError)
                                props.onTwilioRoomError(rsp.error);
                        }
                    });
            }).catch(err => {
                Logger("UI.Twilio.Room.onTrackFetch.Failure", new Date(), JSON.stringify(err), roomName);
                if (props.onTwilioRoomError)
                    props.onTwilioRoomError(err);

            });

        }
    }, [twilioToken, roomName, isAcquiringLocalTracks, getAudioAndVideoTracks]);

    useEffect(() => {
        if (props.error) {
            if (props.onTwilioRoomError)
                props.onTwilioRoomError(props.error);
        }
    }, [props.error]);

    useEffect(() => {
        if (isEndCall) {
            props.onTwilioEndCall();
        }
    }, [isEndCall]);

    useEffect(() => {
        if (_isMounted && _isMounted.current === true) {
            if (screenShareParticipant && screenShareParticipant !== room.localParticipant) {
                setShowControls(false);
            }
        }
    }, [screenShareParticipant, _isMounted]);


    useEffect(() => {
        if (waitingGuestList.length > 0) {
            const roomShareId = waitingGuestList[0].id;
            setWaitingGuestList(state => {
                const waitingGuestStateData = state.map((item, j) => {
                    if (item.id === roomShareId) {
                        return { ...item, showState: true }
                    } else {
                        return item;
                    }
                });
                return waitingGuestStateData;
            });
        }

    }, [waitingGuestList]);

    const onAllowClick = (roomSharedId, isProviderInRoom) => {
        props.allowGuest(roomSharedId);
        if (isProviderInRoom) {
            setGuestAcceptDenyData(prevState => {
                return {
                    ...prevState,
                    showGuestAcceptDenyModal: false
                };
            });
        }
        else {
            manageNextGuestModal(roomSharedId);
        }
    }

    const manageNextGuestModal = (roomSharedId) => {
        const guest = waitingGuestStateData.find(obj => obj.id === roomSharedId);
        const otherWaitingGuests = waitingGuestStateData.filter((item) => item.id !== roomSharedId && item.visited === false);
        const newRoomSharedId = otherWaitingGuests && otherWaitingGuests.length > 0 ? otherWaitingGuests[0].id : null;
        if (!guest) return;
        setWaitingGuestList(state => {
            const waitingGuestStateData = state.map((item) => {
                if (item.id === roomSharedId)
                    return { ...item, showState: false, visited: true }
                else if (newRoomSharedId && item.id === newRoomSharedId)
                    return { ...item, showState: true }
                else
                    return item;
            });
            return waitingGuestStateData;
        });
    }

    const onDenyClick = (roomSharedId, isProviderInRoom) => {
        props.denyGuest(roomSharedId, () => {
            props.getShareLinkHistory(roomName);
        });
        if (isProviderInRoom) {
            setGuestAcceptDenyData(prevState => {
                return {
                    ...prevState,
                    showGuestAcceptDenyModal: false
                };
            });
        }
        else {
            manageNextGuestModal(roomSharedId);
        }
    }

    const onDrawerStateChange = (isDrawerOpen, fullScreenEnabled) => {
        setShowOptions(isDrawerOpen);
        setIsFullScreen(fullScreenEnabled);
    }

    return (
        <div className="twilio-video-container">
            <React.Fragment>
                <div className={showVideoBackground ? "main-container main-participant-container" : "main-container"}
                    onMouseOver={() => setShowControls(true)}
                    onTouchEnd={() => setShowControls(true)}
                    onMouseOut={() => setShowControls(false)}
                >
                    {(isFetching || (localParticipantReconnecting && !roomDisconnected)) && <div className="row token-fetching-loader">
                        <div className="col-12 align-self-center">
                            {(localParticipantReconnecting && !roomDisconnected) && <div className="msg-reconnecting">Something went wrong. We're trying to reconnect you.</div>}
                            <MiniSpinner className="videovisit-mini-spinner" />
                        </div>
                    </div>}
                    {(isAcquiringLocalTracks || isConnecting) && <AllowPermissions />}
                    {roomConnected && <React.Fragment>
                        <div className={`main-view-container ${showControls || showBackdrop ? "show-controls" : "hide-controls"} ${screenShareParticipant ? "screen-share-view" : ""} ${showBackdrop ? "backdrop-enabled" : ""} ${showOptions ? " drawer-open" : ""} ${isFullScreen ? " full-screen-view-provider" : ""}`}>
                            <Room appointmentDetails={props.appointmentDetails} />
                            {screenShareParticipant &&
                                <div className="screen-share-banner">
                                    <div className="screen-share-text">{screenShareParticipant === room.localParticipant ? "You're sharing your screen" : `${screenShareParticipantName} is presenting`}</div>
                                </div>
                            }
                            <Controls setIsEndCall={setIsEndCall} localParticipantRole={props.localParticipantRole} toggleGuestLinkModal={() => toggleGuestLinkModal()}
                                showGuestLinkModal={showGuestLinkModal} onDrawerStateChange={onDrawerStateChange} providerButtons={props.providerButtons}
                                deviceSettingsOpen={deviceSettingsOpen} toggleDeviceSettings={toggleDeviceSettings} isFullScreen={isFullScreen}
                            />
                            {showGuestLinkModal &&
                                <GuestLinkModal showModal={showGuestLinkModal} onDismiss={() => toggleGuestLinkModal(false)}
                                    onLinkSent={(message, isSuccess = true) => toggleGuestLinkModal(false)}
                                    csnIdOfAppointment={props.appointmentDetails.csnId}
                                />
                            }
                            {waitingGuestStateData && (waitingGuestStateData.map(waitingGuest => {
                                return <GuestAllowDenyModal key={waitingGuest.id} userName={waitingGuest.guestName} onAllowClick={() => { onAllowClick(waitingGuest.id, false); }} onDenyClick={() => { onDenyClick(waitingGuest.id, false); }} showModal={waitingGuest.showState} />
                            }))}
                            <GuestAllowDenyModal userName={guestAcceptDenyData.guestName} onAllowClick={() => { onAllowClick(guestAcceptDenyData.roomSharedId, true); }} onDenyClick={() => { onDenyClick(guestAcceptDenyData.roomSharedId, true); }} showModal={guestAcceptDenyData.showGuestAcceptDenyModal} />
                            {(showBackdrop) &&
                                <div className="twilio-video-backdrop" onClick={() => onBackdropClick()} />
                            }
                        </div>
                    </React.Fragment>
                    }
                </div>
            </React.Fragment>
        </div>
    );
}

const mapStateToProps = (state, ownProps) => {
    return {
        guestLinkApprovalStatus: state.videoVisit.guestLinkApprovalStatus,
        shareLinkHistory: state.videoVisit.shareLinkHistory,
        ...ownProps
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        allowGuest: (roomSharedId, callback) => {
            dispatch({ type: types.ALLOW_GUEST, roomShareId: roomSharedId, callback: callback });
        },
        denyGuest: (roomSharedId, callback) => {
            dispatch({ type: types.DENY_GUEST, roomShareId: roomSharedId, callback: callback });
        },
        getShareLinkHistory: (room) => dispatch({ type: types.VIDEO_VISIT_SHARE_LINK_HISTORY, room }),
        setParticipants: (participants) => dispatch({ type: types.SET_PARTICIPANTS, participants })
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(TwilioVideoApp));